import {
  type TokenizeResultError,
  type SecureFormOptions,
  type SecureForms,
  type PayULang,
  SecureForm,
  type PayU,
  TokenType,
} from '@pages/onboarding/types';
import { createContext, useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { toast } from '@ui/components/toaster/toaster';
import { useTranslation } from 'react-i18next';
import React from 'react';

const PAY_U_DEFAULT_COLOR = '#0e1036';
const PAY_U_DISABLED_COLOR = '#918da0';
const PAY_U_PLACEHOLDER_COLOR = '#6b6680';

type TokenizeResult = Promise<
  | {
      token: string;
      mask: string;
    }
  | undefined
>;

export interface PayUTokenize {
  tokenize: (type: TokenType) => TokenizeResult;
}

export type PayURef = React.RefObject<PayUTokenize | undefined | null>;

const getFormsOptions = (lang: PayULang, override?: SecureFormOptions): SecureFormOptions => {
  return {
    frameTitle: 'PayU',
    style: {
      basic: {
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
        fontColor: PAY_U_DEFAULT_COLOR,
      },
      focus: {
        fontColor: PAY_U_DEFAULT_COLOR,
      },
      invalid: {
        fontColor: PAY_U_DEFAULT_COLOR,
      },
      disabled: {
        fontColor: PAY_U_DISABLED_COLOR,
      },
      placeholder: {
        fontColor: PAY_U_PLACEHOLDER_COLOR,
      },
    },
    lang,
    cardIcon: false,
    placeholder: {
      number: '',
      date: '',
      cvv: '',
    },
    ...(override || {}),
  };
};

export interface PayUSecureFormContext {
  payU: PayU;
  secureForms: SecureForms;
  validationErrors: TokenizeResultError['error']['messages'];
  controls: {
    number: SecureForm;
    date: SecureForm;
    cvv: SecureForm;
  };
}

export const PayUContext = createContext<PayUSecureFormContext | undefined>(undefined);

const createPayU = (payUID: string, secureFormOptions: SecureFormOptions, lang: PayULang) => {
  const payU = PayU(payUID);
  const secureForms = payU.secureForms({
    lang,
  });

  const number = secureForms.add('number', secureFormOptions);
  const date = secureForms.add('date', secureFormOptions);
  const cvv = secureForms.add('cvv', secureFormOptions);
  return {
    payU,
    secureForms,
    controls: {
      number,
      date,
      cvv,
    },
  };
};

// create whole payU stuff
export const useCreatePayUContext = (
  payUID?: string,
  ref?: PayURef,
  override?: SecureFormOptions,
) => {
  const { i18n } = useTranslation();
  const [payUContext, setPayUContext] = useState<
    Omit<PayUSecureFormContext, 'validationErrors' | 'reset'> | undefined
  >(undefined);

  const [validationErrors, setValidationErrors] = useState<
    TokenizeResultError['error']['messages']
  >([]);

  const lang = useMemo(() => i18n.language as PayULang, [i18n.language]);
  const secureFormsOptions = useMemo(() => getFormsOptions(lang, override), [lang, override]);

  const tokenize = useCallback(
    async (type: TokenType) => {
      if (!payUContext?.payU) return;
      const response = await payUContext.payU.tokenize(type);

      if (!response) throw new Error('Tokenization failed no response from PayU');
      if (response.status === 'SUCCESS') return response.body;
      if (response.status === 'ERROR') {
        const validation = response.error.messages.filter(
          (message) => message.type === 'validation',
        );
        const technical = response.error.messages.filter((message) => message.type === 'technical');
        setValidationErrors(validation);
        if (technical.length) {
          const technicalError = technical[0];
          toast.error(technicalError?.message);
        }
        throw new Error('Tokenization failed', { cause: response.error.messages });
      }
      throw new Error('Tokenization failed');
    },
    [payUContext],
  );

  useEffect(() => {
    if (!payUID) return;

    const { payU, secureForms, controls } = createPayU(payUID, secureFormsOptions, lang);

    setPayUContext({
      secureForms,
      controls,
      payU,
    });

    return () => {
      setPayUContext(undefined);
    };
  }, [lang, payUID, secureFormsOptions]);

  useEffect(() => {
    if (ref) {
      ref.current = {
        tokenize,
      };
    }
  }, [ref, tokenize]);

  const context = useMemo(() => {
    if (!payUContext) return undefined;
    return {
      ...payUContext,
      validationErrors,
    };
  }, [payUContext, validationErrors]);

  return { context, tokenize };
};

// return payU context inside form and controls
export const usePayUFormContext = () => {
  const context = useContext(PayUContext);
  if (!context) throw new Error('usePayUFormContext must be used within a PayUFormProvider');
  return context;
};

// This component is important to reset payU context when form is unmounted
export const PayUForm = ({
  secureFormOptions,
  children,
  payUID,
  ref,
}: {
  secureFormOptions?: SecureFormOptions;
  children: React.ReactNode;
  payUID: string;
  ref?: PayURef;
}) => {
  const { context } = useCreatePayUContext(payUID, ref, secureFormOptions);

  if (!context) return null;

  return <PayUContext.Provider value={context}>{children}</PayUContext.Provider>;
};
